import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  getEvent,
  addEventNote,
  getClubTeams,
  getTeamAthletes,
  inviteAthletes,
  type Event,
  type EventInvitation,
  type EventNote,
  type EventStatus,
} from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import { ClubNavIcon } from "../../layouts/clubNav";
import AdminPageHeader from "../../components/admin/AdminPageHeader";

interface Team {
  id: string;
  name: string;
}

interface Athlete {
  id: string;
  full_name: string | null;
  email: string;
}

export default function EventDetails() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { activeClubId } = useAuth();
  const { t } = useTranslation();

  const statusLabels: Record<EventStatus, { label: string; color: string }> = {
    draft: { label: t("events.status_draft", "Ciornă"), color: "bg-slate-100 text-slate-700" },
    published: { label: t("events.status_published", "Publicat"), color: "bg-emerald-100 text-emerald-700" },
    cancelled: { label: t("events.status_cancelled", "Anulat"), color: "bg-red-100 text-red-700" },
    completed: { label: t("events.status_completed", "Finalizat"), color: "bg-blue-100 text-blue-700" },
  };

  const invitationStatusLabels: Record<string, { label: string; color: string }> = {
    invited: { label: t("events.inv_invited", "Invitat"), color: "bg-yellow-100 text-yellow-700" },
    confirmed: { label: t("events.inv_confirmed", "Confirmat"), color: "bg-emerald-100 text-emerald-700" },
    declined: { label: t("events.inv_declined", "Refuzat"), color: "bg-red-100 text-red-700" },
  };

  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"invitations" | "notes">("invitations");

  // Notes
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);

  // Invitation filters
  const [invitationStatusFilter, setInvitationStatusFilter] = useState<"all" | "invited" | "confirmed" | "declined">("all");
  const [coachFilter, setCoachFilter] = useState<string>("all");

  // Invite modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>("");
  const [teamAthletes, setTeamAthletes] = useState<Athlete[]>([]);
  const [selectedAthleteIds, setSelectedAthleteIds] = useState<string[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [loadingAthletes, setLoadingAthletes] = useState(false);
  const [sendingInvites, setSendingInvites] = useState(false);

  useEffect(() => {
    if (eventId) {
      fetchEvent();
    }
  }, [eventId]);

  // Load teams when invite modal opens
  useEffect(() => {
    if (showInviteModal && activeClubId) {
      loadTeams();
    }
  }, [showInviteModal, activeClubId]);

  // Load athletes when team is selected
  useEffect(() => {
    if (selectedTeamId) {
      loadTeamAthletes(selectedTeamId);
    } else {
      setTeamAthletes([]);
      setSelectedAthleteIds([]);
    }
  }, [selectedTeamId]);

  const loadTeams = async () => {
    if (!activeClubId) return;
    setLoadingTeams(true);
    try {
      const data = await getClubTeams(activeClubId);
      setTeams(data);
    } catch (error) {
      console.error("Failed to load teams:", error);
      toast.error(t("eventDetails.error_load_teams", "Nu s-au putut încărca echipele"));
    }
    setLoadingTeams(false);
  };

  const loadTeamAthletes = async (teamId: string) => {
    setLoadingAthletes(true);
    try {
      const data = await getTeamAthletes(teamId);
      // Get already invited athlete IDs
      const alreadyInvited = new Set(event?.invitations?.map((inv) => inv.athlete_id) || []);
      // Filter out already invited athletes
      const availableAthletes = data.data.filter((a: Athlete) => !alreadyInvited.has(a.id));
      setTeamAthletes(availableAthletes);
      setSelectedAthleteIds([]);
    } catch (error) {
      console.error("Failed to load athletes:", error);
      toast.error(t("eventDetails.error_load_athletes", "Nu s-au putut încărca sportivii"));
    }
    setLoadingAthletes(false);
  };

  const handleSendInvitations = async () => {
    if (!eventId || !selectedTeamId || selectedAthleteIds.length === 0) return;
    setSendingInvites(true);
    try {
      const result = await inviteAthletes(eventId, selectedTeamId, selectedAthleteIds);
      toast.success(t("eventDetails.invitations_sent", "{{count}} invitații trimise!", { count: result.count }));
      setShowInviteModal(false);
      setSelectedTeamId("");
      setSelectedAthleteIds([]);
      fetchEvent(); // Refresh to show new invitations
    } catch (error: any) {
      console.error("Failed to send invitations:", error);
      toast.error(error.response?.data?.error || t("eventDetails.error_send_invitations", "Nu s-au putut trimite invitațiile"));
    }
    setSendingInvites(false);
  };

  const toggleAthleteSelection = (athleteId: string) => {
    setSelectedAthleteIds((prev) =>
      prev.includes(athleteId) ? prev.filter((id) => id !== athleteId) : [...prev, athleteId]
    );
  };

  const selectAllAthletes = () => {
    setSelectedAthleteIds(teamAthletes.map((a) => a.id));
  };

  const deselectAllAthletes = () => {
    setSelectedAthleteIds([]);
  };

  const fetchEvent = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const data = await getEvent(eventId);
      setEvent(data);
    } catch (error) {
      console.error("Failed to fetch event:", error);
      toast.error(t("eventDetails.error_load_event", "Nu s-a putut încărca evenimentul"));
    }
    setLoading(false);
  };

  const handleAddNote = async () => {
    if (!eventId || !newNote.trim()) return;

    setAddingNote(true);
    try {
      await addEventNote(eventId, newNote.trim());
      toast.success(t("eventDetails.note_added", "Notă adăugată!"));
      setNewNote("");
      fetchEvent(); // Refresh to get new note
    } catch (error: any) {
      console.error("Failed to add note:", error);
      toast.error(error.response?.data?.error || t("eventDetails.error_add_note", "Nu s-a putut adăuga nota"));
    }
    setAddingNote(false);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("ro-RO", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString("ro-RO", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get unique coaches from invitations
  const getUniqueCoaches = () => {
    if (!event?.invitations) return [];
    const coaches = new Map<string, { id: string; name: string }>();
    event.invitations.forEach((inv) => {
      if (inv.coach && !coaches.has(inv.invited_by)) {
        coaches.set(inv.invited_by, {
          id: inv.invited_by,
          name: (inv.coach.full_name || "").trim() || t("eventDetails.unknown", "Necunoscut"),
        });
      }
    });
    return Array.from(coaches.values());
  };

  // Filter invitations
  const filteredInvitations = event?.invitations?.filter((inv) => {
    if (invitationStatusFilter !== "all" && inv.status !== invitationStatusFilter) return false;
    if (coachFilter !== "all" && inv.invited_by !== coachFilter) return false;
    return true;
  }) || [];

  // Group invitations by coach
  const groupedByCoach = filteredInvitations.reduce((acc, inv) => {
    const coachId = inv.invited_by;
    if (!acc[coachId]) {
      acc[coachId] = {
        coach: inv.coach,
        invitations: [],
      };
    }
    acc[coachId].invitations.push(inv);
    return acc;
  }, {} as Record<string, { coach: EventInvitation["coach"]; invitations: EventInvitation[] }>);

  // Stats
  const invitationStats = {
    total: event?.invitations?.length || 0,
    invited: event?.invitations?.filter((i) => i.status === "invited").length || 0,
    confirmed: event?.invitations?.filter((i) => i.status === "confirmed").length || 0,
    declined: event?.invitations?.filter((i) => i.status === "declined").length || 0,
  };

  if (loading) {
    return (
      <ClubAdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-slate-600 font-medium">{t("common.loading", "Se încarcă...")}</span>
          </div>
        </div>
      </ClubAdminLayout>
    );
  }

  if (!event) {
    return (
      <ClubAdminLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <p className="text-slate-500 mb-4">{t("eventDetails.not_found", "Evenimentul nu a fost găsit")}</p>
            <button
              onClick={() => navigate("/dashboard/admin-club/events")}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg"
            >
              {t("eventDetails.back_to_events", "Înapoi la evenimente")}
            </button>
          </div>
        </div>
      </ClubAdminLayout>
    );
  }

  return (
    <ClubAdminLayout>
      <div className="space-y-6">
        <AdminPageHeader
          title={event.title}
          subtitle={t("eventDetails.subtitle", "Detaliile evenimentului, invitații și note.")}
          icon={<ClubNavIcon id="events" className="w-6 h-6" />}
          actions={
            <button
              onClick={() => navigate("/dashboard/admin-club/events")}
              className="px-3 sm:px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t("eventDetails.back_to_events", "Înapoi la evenimente")}
            </button>
          }
        />

        {/* Event Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-4 sm:mb-6">
          {/* Cover Image */}
          <div className="h-36 sm:h-48 bg-gradient-to-br from-blue-500 to-purple-600 relative">
            {event.photo_url ? (
              <img src={event.photo_url} alt={event.title} className="w-full h-full object-cover" />
            ) : (
              <div className="flex items-center justify-center h-full">
                <svg className="w-20 h-20 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            )}
            <div className="absolute top-4 right-4">
              <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${statusLabels[event.status].color}`}>
                {statusLabels[event.status].label}
              </span>
            </div>
          </div>

          {/* Event Info */}
          <div className="p-4 sm:p-6">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800 mb-2 break-words">{event.title}</h1>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-4 text-sm sm:text-base">
              <div className="flex items-center gap-2 text-slate-600">
                <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{formatDate(event.start_date)}</span>
              </div>
              {event.event_time && (
                <div className="flex items-center gap-2 text-slate-600">
                  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{event.event_time}</span>
                </div>
              )}
              {event.location_city && (
                <div className="flex items-center gap-2 text-slate-600">
                  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  </svg>
                  <span>{event.location_city}{event.location_county && `, ${event.location_county}`}</span>
                </div>
              )}
              {event.sport && (
                <div className="flex items-center gap-2 text-slate-600">
                  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  </svg>
                  <span>{event.sport.name}</span>
                </div>
              )}
            </div>

            {event.description && (
              <p className="text-slate-600 mb-4">{event.description}</p>
            )}

            {event.specifications && (
              <div className="p-4 bg-slate-50 rounded-xl">
                <h3 className="font-medium text-slate-700 mb-2">{t("events.specifications", "Specificații")}</h3>
                <p className="text-slate-600 text-sm whitespace-pre-wrap">{event.specifications}</p>
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards — 2 columns on mobile so each card has room for its
            big number instead of being squeezed into a tiny tile. */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 sm:p-4 text-center">
            <p className="text-xl sm:text-3xl font-bold text-slate-800">{invitationStats.total}</p>
            <p className="text-xs sm:text-sm text-slate-500">{t("eventDetails.total_invitations", "Total invitații")}</p>
          </div>
          <div className="bg-yellow-50 rounded-xl border border-yellow-200 p-3 sm:p-4 text-center">
            <p className="text-xl sm:text-3xl font-bold text-yellow-700">{invitationStats.invited}</p>
            <p className="text-xs sm:text-sm text-yellow-600">{t("eventDetails.pending", "În așteptare")}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-3 sm:p-4 text-center">
            <p className="text-xl sm:text-3xl font-bold text-emerald-700">{invitationStats.confirmed}</p>
            <p className="text-xs sm:text-sm text-emerald-600">{t("eventDetails.confirmed", "Confirmați")}</p>
          </div>
          <div className="bg-red-50 rounded-xl border border-red-200 p-3 sm:p-4 text-center">
            <p className="text-xl sm:text-3xl font-bold text-red-700">{invitationStats.declined}</p>
            <p className="text-xs sm:text-sm text-red-600">{t("eventDetails.declined", "Refuzați")}</p>
          </div>
        </div>

        {/* Tabs + Invite Button — stack the row vertically on mobile so the
            invite CTA gets its own full-width line instead of overflowing. */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex gap-2 overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
            <button
              onClick={() => setActiveTab("invitations")}
              className={`flex-shrink-0 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl text-sm sm:text-base font-medium transition-colors whitespace-nowrap ${
                activeTab === "invitations"
                  ? "bg-blue-600 text-white"
                  : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {t("eventDetails.invitations_tab", "Invitații")} ({invitationStats.total})
            </button>
            <button
              onClick={() => setActiveTab("notes")}
              className={`flex-shrink-0 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl text-sm sm:text-base font-medium transition-colors whitespace-nowrap ${
                activeTab === "notes"
                  ? "bg-blue-600 text-white"
                  : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {t("eventDetails.notes_tab", "Note")} ({event.notes?.length || 0})
            </button>
          </div>
          {event.status === "published" && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="w-full sm:w-auto px-4 sm:px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              {t("eventDetails.invite_athletes", "Invită sportivi")}
            </button>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === "invitations" ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t("common.status", "Status")}</label>
                <select
                  value={invitationStatusFilter}
                  onChange={(e) => setInvitationStatusFilter(e.target.value as any)}
                  className="w-full sm:w-auto px-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-800"
                >
                  <option value="all">{t("common.all", "Toate")}</option>
                  <option value="invited">{t("eventDetails.pending", "În așteptare")}</option>
                  <option value="confirmed">{t("eventDetails.filter_confirmed", "Confirmate")}</option>
                  <option value="declined">{t("eventDetails.filter_declined", "Refuzate")}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t("events.coaches", "Antrenor")}</label>
                <select
                  value={coachFilter}
                  onChange={(e) => setCoachFilter(e.target.value)}
                  className="w-full sm:w-auto px-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-800"
                >
                  <option value="all">{t("eventDetails.all_coaches", "Toți antrenorii")}</option>
                  {getUniqueCoaches().map((coach) => (
                    <option key={coach.id} value={coach.id}>{coach.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Invitations grouped by coach */}
            {Object.keys(groupedByCoach).length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <svg className="w-16 h-16 mx-auto mb-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p>{t("eventDetails.no_invitations", "Nicio invitație găsită")}</p>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedByCoach).map(([coachId, { coach, invitations }]) => (
                  <div key={coachId} className="border border-slate-200 rounded-xl overflow-hidden">
                    <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                      <h3 className="font-semibold text-slate-800">
                        {t("eventDetails.invited_by", "Invitate de:")} {coach?.full_name || t("eventDetails.unknown", "Necunoscut")}
                      </h3>
                      <p className="text-sm text-slate-500">
                        {invitations.length} {t("eventDetails.athletes_count", "sportivi")}
                      </p>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {invitations.map((inv) => (
                        <div key={inv.id} className="px-4 py-3 flex items-center justify-between hover:bg-slate-50">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center text-slate-600 font-medium">
                              {(inv.athlete?.full_name || inv.athlete?.email || "?").charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">
                                {inv.athlete?.full_name || inv.athlete?.email || t("eventDetails.unknown", "Necunoscut")}
                              </p>
                              <p className="text-sm text-slate-500">
                                {inv.team?.name || t("eventDetails.unknown_team", "Echipă necunoscută")}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {inv.status === "confirmed" && inv.confirmed_at && (
                              <span className="text-xs text-slate-400">
                                {formatDateTime(inv.confirmed_at)}
                              </span>
                            )}
                            {inv.status === "declined" && inv.decline_reason && (
                              <span className="text-xs text-slate-400 max-w-[200px] truncate" title={inv.decline_reason}>
                                {t("eventDetails.reason", "Motiv:")} {inv.decline_reason}
                              </span>
                            )}
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${invitationStatusLabels[inv.status].color}`}>
                              {invitationStatusLabels[inv.status].label}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            {/* Add Note Form */}
            {event.status === "published" && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t("eventDetails.add_note", "Adaugă o notă")}
                </label>
                <div className="flex gap-3">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="flex-1 px-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-800 placeholder-slate-400 resize-none"
                    rows={3}
                    placeholder={t("eventDetails.note_placeholder", "Scrie o notă pentru participanți...")}
                  />
                  <button
                    onClick={handleAddNote}
                    disabled={addingNote || !newNote.trim()}
                    className="px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 self-end"
                  >
                    {addingNote ? t("eventDetails.sending", "Se trimite...") : t("common.submit", "Trimite")}
                  </button>
                </div>
              </div>
            )}

            {/* Notes List */}
            {!event.notes || event.notes.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <svg className="w-16 h-16 mx-auto mb-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <p>{t("eventDetails.no_notes", "Nicio notă adăugată încă")}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {event.notes.map((note) => (
                  <div
                    key={note.id}
                    className={`p-4 rounded-xl border ${
                      note.author_role === "club_admin"
                        ? "bg-blue-50 border-blue-200"
                        : "bg-emerald-50 border-emerald-200"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          note.author_role === "club_admin"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-emerald-100 text-emerald-700"
                        }`}>
                          {note.author_role === "club_admin" ? t("roles.club_admin", "Admin") : t("roles.coach", "Antrenor")}
                        </span>
                        <span className="font-medium text-slate-800">
                          {note.author?.full_name || ""}
                        </span>
                      </div>
                      <span className="text-xs text-slate-500">
                        {formatDateTime(note.created_at)}
                      </span>
                    </div>
                    <p className="text-slate-700 whitespace-pre-wrap">{note.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Invite Athletes Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-800">{t("eventDetails.invite_athletes", "Invită sportivi")}</h2>
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setSelectedTeamId("");
                  setSelectedAthleteIds([]);
                }}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              >
                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Team Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t("eventDetails.select_team", "Selectează echipa")}
                </label>
                {loadingTeams ? (
                  <div className="flex items-center gap-2 py-3 text-slate-500">
                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    {t("eventDetails.loading_teams", "Se încarcă echipele...")}
                  </div>
                ) : (
                  <select
                    value={selectedTeamId}
                    onChange={(e) => setSelectedTeamId(e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-slate-700"
                  >
                    <option value="">{t("eventDetails.select_team_placeholder", "— Selectează o echipă —")}</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Athletes Selection */}
              {selectedTeamId && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-slate-700">
                      {t("eventDetails.select_athletes", "Selectează sportivii")}
                    </label>
                    {teamAthletes.length > 0 && (
                      <div className="flex gap-2">
                        <button
                          onClick={selectAllAthletes}
                          className="text-xs text-blue-600 hover:text-blue-700"
                        >
                          {t("eventDetails.select_all", "Selectează tot")}
                        </button>
                        <span className="text-slate-300">|</span>
                        <button
                          onClick={deselectAllAthletes}
                          className="text-xs text-slate-500 hover:text-slate-700"
                        >
                          {t("eventDetails.deselect_all", "Deselectează tot")}
                        </button>
                      </div>
                    )}
                  </div>

                  {loadingAthletes ? (
                    <div className="flex items-center gap-2 py-6 text-slate-500 justify-center">
                      <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      {t("eventDetails.loading_athletes", "Se încarcă sportivii...")}
                    </div>
                  ) : teamAthletes.length === 0 ? (
                    <div className="text-center py-6 text-slate-500 bg-slate-50 rounded-xl">
                      <svg className="w-12 h-12 mx-auto mb-2 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <p>{t("eventDetails.all_already_invited", "Toți sportivii din această echipă au fost deja invitați")}</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {teamAthletes.map((athlete) => (
                        <label
                          key={athlete.id}
                          className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                            selectedAthleteIds.includes(athlete.id)
                              ? "border-blue-500 bg-blue-50"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedAthleteIds.includes(athlete.id)}
                            onChange={() => toggleAthleteSelection(athlete.id)}
                            className="w-5 h-5 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-slate-800">
                              {athlete.full_name || t("eventDetails.no_name", "Fără nume")}
                            </p>
                            <p className="text-sm text-slate-500">{athlete.email}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Selected count */}
              {selectedAthleteIds.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                  <p className="text-blue-800 font-medium">
                    {t("eventDetails.athletes_selected", "{{count}} sportivi selectați pentru invitare", { count: selectedAthleteIds.length })}
                  </p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex gap-3">
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setSelectedTeamId("");
                  setSelectedAthleteIds([]);
                }}
                className="flex-1 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors"
              >
                {t("common.cancel", "Anulează")}
              </button>
              <button
                onClick={handleSendInvitations}
                disabled={sendingInvites || selectedAthleteIds.length === 0}
                className="flex-1 py-2.5 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {sendingInvites ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    {t("eventDetails.sending_invitations", "Se trimit...")}
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    {t("eventDetails.send_invitations", "Trimite invitații")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </ClubAdminLayout>
  );
}
